import { Alert, Box } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import type { AssetRenderComponentProps, ReactAssetNavigation } from '../assetTypes'
import type { ParamValues } from '../assetParamsBus'
import { cloneJsonObject } from '../shared/jsonValues'
import { ConfigRouteResolverContext } from './ConfigRouteResolverContext'
import { resolveConfigDefinition } from './configDefinition'

type ConfigRoute = {
  feature: string
  params: ParamValues
}

const cloneRoute = (route: ConfigRoute): ConfigRoute => ({
  feature: route.feature,
  params: cloneJsonObject(route.params),
})

const ConfigAssetRenderer = ({ content, trellis, onLog }: AssetRenderComponentProps) => {
  const resolution = useMemo(() => {
    try {
      return {
        resolved: resolveConfigDefinition(content),
        error: null,
      }
    } catch (error) {
      return {
        resolved: null,
        error: error instanceof Error ? error.message : String(error),
      }
    }
  }, [content])

  useEffect(() => {
    if (!resolution.error) return
    onLog?.(`Config resolve error: ${resolution.error}`)
  }, [onLog, resolution.error])

  const rootRoute = useMemo<ConfigRoute | null>(() => {
    if (!resolution.resolved) return null
    return {
      feature: resolution.resolved.root.asset,
      params: cloneJsonObject(resolution.resolved.root.params),
    }
  }, [resolution.resolved])

  const [routeStack, setRouteStack] = useState<ConfigRoute[]>(() => (rootRoute ? [cloneRoute(rootRoute)] : []))

  useEffect(() => {
    setRouteStack(rootRoute ? [cloneRoute(rootRoute)] : [])
  }, [rootRoute])

  const navigation = useMemo<ReactAssetNavigation>(
    () => ({
      push(input) {
        if (!resolution.resolved) return
        try {
          const nextRoute = resolution.resolved.resolveRoute({
            feature: typeof input?.feature === 'string' ? input.feature : '',
            params: cloneJsonObject(input.params),
          })
          setRouteStack((previous) => [
            ...previous,
            {
              feature: nextRoute.asset,
              params: cloneJsonObject(nextRoute.params),
            },
          ])
        } catch (error) {
          onLog?.(`Config navigation resolve error: ${error instanceof Error ? error.message : String(error)}`)
        }
      },
      pop() {
        setRouteStack((previous) => (previous.length > 1 ? previous.slice(0, -1) : previous))
      },
      resetToRoot() {
        setRouteStack((previous) => (previous.length > 0 ? [previous[0]] : previous))
      },
      canPop() {
        return routeStack.length > 1
      },
    }),
    [onLog, resolution.resolved, routeStack.length]
  )

  const currentRoute = routeStack[routeStack.length - 1] ?? rootRoute
  const InlineFeature = trellis?.features?.Inline

  if (!trellis) {
    return <Alert severity="error">Config runtime bridge is unavailable.</Alert>
  }

  if (!InlineFeature) {
    return <Alert severity="error">Config assets require the host runtime to provide trellis.features.Inline.</Alert>
  }

  if (resolution.error || !currentRoute) {
    return <Alert severity="error">{resolution.error ?? 'Config payload could not be resolved.'}</Alert>
  }

  const resolvedConfig = resolution.resolved
  if (!resolvedConfig) {
    return <Alert severity="error">Config payload could not be resolved.</Alert>
  }

  return (
    <ConfigRouteResolverContext.Provider value={resolvedConfig.resolveRoute}>
      <Box sx={{ height: '100%', width: '100%', overflow: 'hidden' }}>
        <InlineFeature
          feature={currentRoute.feature}
          params={cloneJsonObject(currentRoute.params)}
          navigation={navigation}
        />
      </Box>
    </ConfigRouteResolverContext.Provider>
  )
}

export default ConfigAssetRenderer
